import { TradingReportView } from './TradingReportView';
import { reportSections } from './reportModel';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import type { components } from '@adiox/api';
import { formatServerDateTime, useApi, useDisplayZone, useMoneyFormat, useServerOffset } from '@adiox/ui';
import { useSymbols } from '../market/useSymbols';
import { useStream } from '../stream/stream';
import { useActiveLogin } from '../trading/useTrading';
import { toCsv, downloadCsv, type CsvColumn } from '@adiox/ui';
import { EquityChart } from './EquityChart';
import { PRESETS, rangeFor, rangeProblem, type Preset, type Range } from './range';
import { summaryItems } from './summary';
import { useLayout } from '../shell/layout';
import { MobileHistoryView } from './MobileHistoryView';

type Deal = components['schemas']['DealRecord'];
type Order = components['schemas']['OrderRecord'];
type Position = components['schemas']['ClosedPosition'];
type Tab = 'deals' | 'orders' | 'positions' | 'equity' | 'report';
/** The tabs that show a list of rows (the others are a picture or a report). */
const isList = (tab: Tab): tab is 'deals' | 'orders' | 'positions' => tab !== 'equity' && tab !== 'report';

const TABS: { id: Tab; label: string }[] = [
  { id: 'deals', label: 'Deals' },
  { id: 'orders', label: 'Orders' },
  { id: 'positions', label: 'Positions' },
  { id: 'equity', label: 'Equity' },
  { id: 'report', label: 'Report' },
];

const PAGE = 100;
const signClass = (v: number) => (v > 0 ? 'text-up' : v < 0 ? 'text-down' : '');

interface Column<T> extends CsvColumn<T> {
  align?: 'right';
  className?: (row: T) => string;
  show?: (row: T) => string;
}

/** Times are shown on the server clock, like the top bar; the zone is in the header so an export says what it is. */
const dealColumns = (
  time: (iso: string | null | undefined) => string,
  zone: string,
  fmt: (v: number) => string,
): Column<Deal>[] => [
  { header: `Time (${zone})`, value: (r) => time(r.time) },
  { header: 'Deal', value: (r) => r.ticket },
  { header: 'Order', value: (r) => r.order },
  { header: 'Position', value: (r) => r.positionId },
  { header: 'Symbol', value: (r) => r.symbol },
  { header: 'Type', value: (r) => r.type.toLowerCase() },
  { header: 'Direction', value: (r) => r.entry.toLowerCase() },
  { header: 'Volume', value: (r) => r.volume, align: 'right' },
  { header: 'Price', value: (r) => r.price, align: 'right' },
  { header: 'Commission', value: (r) => r.commission, align: 'right', show: (r) => fmt(r.commission) },
  { header: 'Swap', value: (r) => r.swap, align: 'right', show: (r) => fmt(r.swap) },
  {
    header: 'Profit',
    value: (r) => r.profit,
    align: 'right',
    show: (r) => fmt(r.profit),
    className: (r) => signClass(r.profit),
  },
  { header: 'Reason', value: (r) => r.reason },
  { header: 'Comment', value: (r) => r.comment },
];

const orderColumns = (time: (iso: string | null | undefined) => string, zone: string): Column<Order>[] => [
  { header: `Time (${zone})`, value: (r) => time(r.timeSetup) },
  { header: 'Order', value: (r) => r.ticket },
  { header: 'Symbol', value: (r) => r.symbol },
  { header: 'Type', value: (r) => r.type.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase() },
  { header: 'Volume', value: (r) => r.volumeInitial, align: 'right' },
  { header: 'Filled', value: (r) => r.volumeInitial - r.volumeCurrent, align: 'right' },
  { header: 'Price', value: (r) => r.priceOrder, align: 'right' },
  {
    header: 'S/L',
    value: (r) => r.stopLoss,
    align: 'right',
    show: (r) => (r.stopLoss > 0 ? String(r.stopLoss) : ''),
  },
  {
    header: 'T/P',
    value: (r) => r.takeProfit,
    align: 'right',
    show: (r) => (r.takeProfit > 0 ? String(r.takeProfit) : ''),
  },
  { header: 'Status', value: (r) => r.status },
  { header: `Done (${zone})`, value: (r) => time(r.timeDone) },
  { header: 'Comment', value: (r) => r.comment },
];

const positionColumns = (
  time: (iso: string | null | undefined) => string,
  zone: string,
  fmt: (v: number) => string,
): Column<Position>[] => [
  { header: `Opened (${zone})`, value: (r) => time(r.timeOpen) },
  { header: 'Position', value: (r) => r.ticket },
  { header: 'Symbol', value: (r) => r.symbol },
  { header: 'Type', value: (r) => r.side.toLowerCase() },
  { header: 'Volume', value: (r) => r.volume, align: 'right' },
  { header: 'Open price', value: (r) => r.priceOpen, align: 'right' },
  {
    header: 'S/L',
    value: (r) => r.stopLoss,
    align: 'right',
    show: (r) => (r.stopLoss > 0 ? String(r.stopLoss) : ''),
  },
  {
    header: 'T/P',
    value: (r) => r.takeProfit,
    align: 'right',
    show: (r) => (r.takeProfit > 0 ? String(r.takeProfit) : ''),
  },
  { header: `Closed (${zone})`, value: (r) => time(r.timeClose) },
  { header: 'Close price', value: (r) => (r.timeClose ? r.priceClose : null), align: 'right' },
  { header: 'Commission', value: (r) => r.commission, align: 'right', show: (r) => fmt(r.commission) },
  { header: 'Swap', value: (r) => r.swap, align: 'right', show: (r) => fmt(r.swap) },
  {
    header: 'Profit',
    value: (r) => r.profit,
    align: 'right',
    show: (r) => fmt(r.profit),
    className: (r) => signClass(r.profit),
  },
  { header: 'Comment', value: (r) => r.comment },
];

function useHistory(tab: Exclude<Tab, 'equity'>, login: number | undefined, range: Range, symbol: string) {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: ['history', tab, login, range, symbol],
    enabled: login !== undefined,
    initialPageParam: undefined as number | undefined,
    queryFn: async ({ pageParam }) => {
      const query = {
        ...range,
        ...(symbol ? { symbol } : {}),
        limit: PAGE,
        ...(pageParam !== undefined ? { cursor: pageParam } : {}),
      };
      const path = { login: login! };
      if (tab === 'deals') {
        const { data } = await api.GET('/api/v1/accounts/{login}/history/deals', { params: { path, query } });
        if (!data) throw new Error('deals failed');
        return data as { items: unknown[]; nextCursor?: number | null };
      }
      if (tab === 'orders') {
        const { data } = await api.GET('/api/v1/accounts/{login}/history/orders', {
          params: { path, query },
        });
        if (!data) throw new Error('orders failed');
        return data as { items: unknown[]; nextCursor?: number | null };
      }
      const { data } = await api.GET('/api/v1/accounts/{login}/history/positions', {
        params: { path, query },
      });
      if (!data) throw new Error('positions failed');
      return data as { items: unknown[]; nextCursor?: number | null };
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

function Table<T>({ rows, columns, label }: { rows: T[]; columns: Column<T>[]; label: string }) {
  return (
    <table className="w-full text-sm" aria-label={label}>
      <thead className="sticky top-0 bg-panel">
        <tr className="border-b border-line">
          {columns.map((c) => (
            <th
              key={c.header}
              scope="col"
              className={`px-2 py-1 text-xs font-normal uppercase tracking-wide text-muted ${c.align === 'right' ? 'text-right' : 'text-left'}`}
            >
              {c.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className="border-b border-line/40 hover:bg-panel2" data-testid="history-row">
            {columns.map((c) => (
              <td
                key={c.header}
                className={`num whitespace-nowrap px-2 py-1 ${c.align === 'right' ? 'text-right' : ''} ${c.className?.(row) ?? ''}`}
              >
                {c.show ? c.show(row) : String(c.value(row) ?? '')}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Deals, orders and closed positions with a date filter and paging, the daily equity curve, and CSV export of what is shown. */
export function HistoryPanel() {
  const api = useApi();
  const login = useActiveLogin();
  const queryClient = useQueryClient();
  const currency = useStream((s) => (login === undefined ? '' : (s.accounts[login]?.currency ?? '')));
  const lastEvent = useStream((s) => s.recentEvents.at(-1)?.seq ?? 0);
  const { mobile } = useLayout();
  // On a phone the list tabs are the only ones (Positions first, as MetaTrader's phone terminal opens it).
  const [tab, setTab] = useState<Tab>(mobile ? 'positions' : 'deals');
  const listTab = isList(tab) ? tab : mobile ? 'positions' : 'deals';
  const [preset, setPreset] = useState<Preset>('7d');
  const [symbol, setSymbol] = useState('');
  const symbols = useSymbols().data;
  const balance = useStream((s) =>
    login === undefined ? null : (s.accounts[login]?.figures.balance ?? null),
  );
  const [custom, setCustom] = useState({ from: '', to: '' });
  const [now] = useState(() => Date.now());
  // Times and the day filters follow the chosen time zone; the equity curve is by server day, as the server keeps it.
  const zone = useDisplayZone();
  const offset = zone.offsetAt(now);
  const serverOffset = useServerOffset() ?? 0;
  const fmt = useMoneyFormat();
  const problem = preset === 'custom' ? rangeProblem(custom) : null;
  const range = useMemo(
    () => (problem ? {} : rangeFor(preset, now, custom, offset)),
    [preset, now, custom, problem, offset],
  );
  const columns = useMemo(() => {
    const time = (iso: string | null | undefined) => formatServerDateTime(iso, zone.offsetAt);
    return {
      deals: dealColumns(time, zone.label, fmt),
      orders: orderColumns(time, zone.label),
      positions: positionColumns(time, zone.label, fmt),
    };
  }, [zone, fmt]);

  // New deals and orders arrive by stream: refresh what is shown.
  const seen = useRef(lastEvent);
  useEffect(() => {
    if (lastEvent === seen.current) return;
    seen.current = lastEvent;
    void queryClient.invalidateQueries({ queryKey: ['history'] });
  }, [lastEvent, queryClient]);

  const listing = useHistory(listTab, isList(tab) || mobile ? login : undefined, range, symbol);
  // The totals of the whole period (not only the pages loaded), from the server.
  const summary = useQuery({
    queryKey: ['history', 'summary', login, range, symbol],
    enabled: (isList(tab) || mobile) && login !== undefined && !problem,
    placeholderData: (previous) => previous,
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/accounts/{login}/history/summary', {
        params: { path: { login: login! }, query: { ...range, ...(symbol ? { symbol } : {}) } },
      });
      if (!data) throw new Error('summary failed');
      return data;
    },
  });
  const equity = useQuery({
    queryKey: ['history', 'equity', login, range],
    enabled: tab === 'equity' && login !== undefined,
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/accounts/{login}/history/equity', {
        // The equity curve is by server-time day: this endpoint takes dates (yyyy-MM-dd), not date-times.
        params: {
          path: { login: login! },
          query: {
            from: range.from ? formatServerDateTime(range.from, serverOffset).slice(0, 10) : undefined,
            to: range.to ? formatServerDateTime(range.to, serverOffset).slice(0, 10) : undefined,
          },
        },
      });
      if (!data) throw new Error('equity failed');
      return data;
    },
  });

  // MT5's trading report over the period, for the whole account.
  const report = useQuery({
    queryKey: ['history', 'report', login, range],
    enabled: tab === 'report' && login !== undefined && !problem,
    placeholderData: (previous) => previous,
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/accounts/{login}/history/report', {
        params: { path: { login: login! }, query: range },
      });
      if (!data) throw new Error('report failed');
      return data;
    },
  });

  const rows = useMemo(() => (listing.data?.pages ?? []).flatMap((p) => p.items), [listing.data]);

  const exportCsv = () => {
    const stamp = new Date().toISOString().slice(0, 10);
    if (tab === 'report') {
      if (!report.data) return;
      const lines = reportSections(report.data, fmt).flatMap((s) =>
        s.lines.map((l) => ({ section: s.title, label: l.label, value: l.value })),
      );
      const bySymbol = report.data.bySymbol.map((b) => ({
        section: 'By symbol',
        label: b.symbol,
        value: `${b.stats.trades} trades, ${b.stats.winRate}% won, net ${fmt(b.stats.netProfit)}`,
      }));
      downloadCsv(
        `report-${stamp}.csv`,
        toCsv(
          [...lines, ...bySymbol],
          [
            { header: 'Section', value: (r) => r.section },
            { header: 'Figure', value: (r) => r.label },
            { header: 'Value', value: (r) => r.value },
          ],
        ),
      );
    } else if (tab === 'equity') {
      downloadCsv(
        `equity-${stamp}.csv`,
        toCsv(equity.data ?? [], [
          { header: 'Day', value: (r) => r.day },
          { header: 'Balance', value: (r) => r.balance },
          { header: 'Equity', value: (r) => r.equity },
        ]),
      );
    } else {
      downloadCsv(`${tab}-${stamp}.csv`, toCsv(rows, columns[tab] as Column<unknown>[]));
    }
  };

  const busy = tab === 'report' ? report.isPending : tab === 'equity' ? equity.isPending : listing.isPending;
  const failed = tab === 'report' ? report.isError : tab === 'equity' ? equity.isError : listing.isError;
  const count =
    tab === 'report'
      ? (report.data?.all.trades ?? 0)
      : tab === 'equity'
        ? (equity.data?.length ?? 0)
        : rows.length;
  const control =
    'rounded-md border border-line bg-panel2 px-2 py-1 text-sm outline-none focus:border-accent';

  if (login === undefined) return <p className="p-4 text-sm text-muted">No trading account.</p>;

  if (mobile) {
    return (
      <MobileHistoryView
        tab={listTab}
        onTab={setTab}
        rows={rows}
        busy={listing.isPending}
        failed={listing.isError}
        onRetry={() => void listing.refetch()}
        hasNext={listing.hasNextPage}
        fetchingNext={listing.isFetchingNextPage}
        onMore={() => void listing.fetchNextPage()}
        summary={summary.data}
        balance={balance}
        preset={preset}
        onPreset={setPreset}
        custom={custom}
        onCustom={setCustom}
        problem={problem}
        symbol={symbol}
        onSymbol={setSymbol}
        fmt={fmt}
        time={(iso) => formatServerDateTime(iso, zone.offsetAt)}
      />
    );
  }

  return (
    <section className="flex h-full flex-col bg-panel" aria-label="History">
      <div className="flex flex-wrap items-center gap-2 border-b border-line p-2">
        <div
          role="tablist"
          aria-label="History type"
          className="flex overflow-hidden rounded-md border border-line"
        >
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => setTab(t.id)}
              className={`px-3 py-1 text-xs font-semibold ${tab === t.id ? 'bg-accent text-accent-fg' : 'hover:bg-panel2'}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <label className="sr-only" htmlFor="history-symbol">
          Symbol
        </label>
        <select
          id="history-symbol"
          className={control}
          hidden={!isList(tab)}
          value={symbol}
          onChange={(e) => setSymbol(e.target.value)}
        >
          <option value="">All symbols</option>
          {(symbols ?? []).map((s) => (
            <option key={s.name} value={s.name}>
              {s.name}
            </option>
          ))}
        </select>
        <label className="sr-only" htmlFor="history-range">
          Period
        </label>
        <select
          id="history-range"
          className={control}
          value={preset}
          onChange={(e) => setPreset(e.target.value as Preset)}
        >
          {PRESETS.map((p) => (
            <option key={p.id} value={p.id}>
              {p.label}
            </option>
          ))}
        </select>
        {preset === 'custom' && (
          <>
            <label className="sr-only" htmlFor="history-from">
              From date
            </label>
            <input
              id="history-from"
              type="date"
              className={control}
              value={custom.from}
              onChange={(e) => setCustom({ ...custom, from: e.target.value })}
            />
            <span className="text-muted">to</span>
            <label className="sr-only" htmlFor="history-to">
              To date
            </label>
            <input
              id="history-to"
              type="date"
              className={control}
              value={custom.to}
              onChange={(e) => setCustom({ ...custom, to: e.target.value })}
            />
          </>
        )}
        <span className="flex-1" />
        <span className="text-xs text-muted" data-testid="history-count">
          {tab === 'report'
            ? `${count} trade${count === 1 ? '' : 's'}`
            : `${count} shown${listing.hasNextPage && isList(tab) ? ' (more available)' : ''}`}
        </span>
        <button
          type="button"
          onClick={exportCsv}
          disabled={tab === 'report' ? !report.data : count === 0}
          className="rounded-md border border-line px-3 py-1 text-xs font-semibold hover:border-accent disabled:opacity-40"
        >
          Export CSV
        </button>
      </div>
      {problem && (
        <p role="alert" className="px-3 py-1 text-sm text-down">
          {problem}
        </p>
      )}
      {isList(tab) && summary.data && (
        <dl
          className="flex flex-wrap gap-x-5 gap-y-1 border-b border-line px-3 py-1.5 text-sm"
          aria-label="Totals for this period"
          data-testid="history-summary"
        >
          {summaryItems(summary.data, symbol !== '', balance).map((i) => (
            <div key={i.id} className="flex items-baseline gap-1.5" data-testid={`summary-${i.id}`}>
              <dt className="text-xs uppercase tracking-wide text-muted">{i.label}</dt>
              <dd
                className={`num font-semibold ${i.signed ? (i.value > 0 ? 'text-up' : i.value < 0 ? 'text-down' : '') : ''}`}
              >
                {fmt(i.value)}
              </dd>
            </div>
          ))}
          <div className="flex items-baseline gap-1.5 text-xs text-muted">
            {summary.data.trades} closing deal{summary.data.trades === 1 ? '' : 's'} · {currency}
          </div>
        </dl>
      )}
      {/* a scrolling area must be reachable by keyboard */}
      <div className="min-h-0 flex-1 overflow-auto" tabIndex={0} role="group" aria-label="History list">
        {busy ? (
          <p className="p-4 text-sm text-muted">Loading…</p>
        ) : failed ? (
          <div role="alert" className="flex items-center gap-3 p-4 text-sm text-down">
            <span>Could not load the history.</span>
            <button
              type="button"
              className="rounded-md border border-line px-3 py-1 text-sm text-fg hover:border-accent"
              onClick={() =>
                void (tab === 'report'
                  ? report.refetch()
                  : tab === 'equity'
                    ? equity.refetch()
                    : listing.refetch())
              }
            >
              Retry
            </button>
          </div>
        ) : tab === 'report' ? (
          report.data && <TradingReportView report={report.data} money={fmt} />
        ) : tab === 'equity' ? (
          <EquityChart points={equity.data ?? []} currency={currency} />
        ) : count === 0 ? (
          <p className="p-4 text-sm text-muted">Nothing in this period.</p>
        ) : (
          <>
            {tab === 'deals' && <Table rows={rows as Deal[]} columns={columns.deals} label="Deals" />}
            {tab === 'orders' && <Table rows={rows as Order[]} columns={columns.orders} label="Orders" />}
            {tab === 'positions' && (
              <Table rows={rows as Position[]} columns={columns.positions} label="Closed positions" />
            )}
            {listing.hasNextPage && (
              <div className="p-3">
                <button
                  type="button"
                  className="rounded-md border border-line px-3 py-1 text-sm hover:border-accent disabled:opacity-40"
                  disabled={listing.isFetchingNextPage}
                  onClick={() => void listing.fetchNextPage()}
                >
                  {listing.isFetchingNextPage ? 'Loading…' : 'Load more'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
